import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon,
  ShieldIcon, CheckIcon, VideoIcon,
} from '../assets/icons/Icons';
import '../styles/auth.css';
import '../styles/forgot-password.css';

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const otpRefs = useRef([]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      await new Promise(r => setTimeout(r, 1000));
      setStatus('');
      setStep(2);
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    if (pasted.length > 0) otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.join('').length < 6) { setErrorMsg('Please enter the full 6-digit code'); return; }
    setStatus('sending');
    setErrorMsg('');
    try {
      await new Promise(r => setTimeout(r, 1000));
      setStatus('');
      setStep(3);
    } catch (err) {
      setStatus('error');
      setErrorMsg('Invalid code. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    setOtp(['', '', '', '', '', '']);
    setErrorMsg('');
    setStatus('sending');
    try {
      await new Promise(r => setTimeout(r, 1000));
      setStatus('');
    } catch (err) { setStatus('error'); }
  };

  const getStrength = () => {
    if (!password) return { level: 0, label: '', cls: '' };
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    if (s <= 1) return { level: 1, label: 'Weak', cls: 'weak' };
    if (s <= 2) return { level: 2, label: 'Medium', cls: 'medium' };
    if (s <= 3) return { level: 3, label: 'Good', cls: 'medium' };
    return { level: 4, label: 'Strong', cls: 'strong' };
  };
  const strength = getStrength();

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setErrorMsg('Passwords do not match'); return; }
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters'); return; }
    setStatus('sending');
    setErrorMsg('');
    try {
      await new Promise(r => setTimeout(r, 1000));
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="auth-page">

      {/* Left Panel — Decorative (same as Login) */}
      <div className="auth-panel">
        <div className="auth-panel-header">
          <Link to="/" className="logo">
            <div className="logo-icon">
              <HeartIcon size={20} color="white" />
            </div>
            <span className="logo-text">Ishira</span>
          </Link>
        </div>

        <div className="auth-panel-content">
          <h1>Reset your <em>password</em></h1>
          <p>
            Don't worry — it happens to the best of us. We'll have you
            back in your account in no time.
          </p>
        </div>

        <div className="auth-panel-features">
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <ShieldIcon size={16} />
            </div>
            <span>Secure verification process</span>
          </div>
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <MailIcon size={16} />
            </div>
            <span>OTP sent to your email</span>
          </div>
          <div className="auth-feature">
            <div className="auth-feature-icon">
              <CheckIcon size={16} />
            </div>
            <span>Reset in under 2 minutes</span>
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="auth-form-panel">
        <div className="auth-form-container">

          {/* Step indicator */}
          <div className="fp-steps">
            <div className={`fp-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
              <div className="fp-step-dot">{step > 1 ? '✓' : '1'}</div>
              <span>Email</span>
            </div>
            <div className={`fp-step-line ${step > 1 ? 'done' : ''}`} />
            <div className={`fp-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`}>
              <div className="fp-step-dot">{step > 2 ? '✓' : '2'}</div>
              <span>Verify</span>
            </div>
            <div className={`fp-step-line ${step > 2 ? 'done' : ''}`} />
            <div className={`fp-step ${step >= 3 ? 'active' : ''} ${status === 'success' ? 'done' : ''}`}>
              <div className="fp-step-dot">{status === 'success' ? '✓' : '3'}</div>
              <span>Reset</span>
            </div>
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <>
              <div className="auth-form-header">
                <h2>Forgot your password?</h2>
                <p>Enter the email linked to your account and we'll send a verification code.</p>
              </div>

              <form className="auth-form" onSubmit={handleSendOtp}>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      autoComplete="email"
                    />
                    <span className="input-icon"><MailIcon size={16} /></span>
                  </div>
                </div>

                {errorMsg && <div className="fp-error">{errorMsg}</div>}

                <button type="submit" className="auth-submit" disabled={status === 'sending' || !email}>
                  {status === 'sending' ? 'Sending...' : 'Send verification code'}
                </button>
              </form>

              {/* Phase-1 launch: login disabled — cross-link hidden */}
              {/* <p className="fp-alt-link">
                Remember your password? <Link to="/login">Sign in</Link>
              </p> */}
            </>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <>
              <div className="auth-form-header">
                <h2>Check your email</h2>
                <p>We sent a 6-digit code to <strong>{email}</strong></p>
              </div>

              <form className="auth-form" onSubmit={handleVerifyOtp}>
                <div className="otp-input-group" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => otpRefs.current[i] = el}
                      className="otp-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                {errorMsg && <div className="fp-error">{errorMsg}</div>}

                <button type="submit" className="auth-submit" disabled={status === 'sending' || otp.join('').length < 6}>
                  {status === 'sending' ? 'Verifying...' : 'Verify code'}
                </button>
              </form>

              <p className="fp-alt-link">
                Didn't receive the code?{' '}
                <button className="fp-link-btn" onClick={handleResendOtp} disabled={status === 'sending'}>Resend code</button>
              </p>
              <p className="fp-alt-link">
                <button className="fp-link-btn" onClick={() => { setStep(1); setErrorMsg(''); }}>← Use a different email</button>
              </p>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 3 && status !== 'success' && (
            <>
              <div className="auth-form-header">
                <h2>Create new password</h2>
                <p>Your identity has been verified. Set a new password for your account.</p>
              </div>

              <form className="auth-form" onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label className="form-label">New password</label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoFocus
                      autoComplete="new-password"
                    />
                    <span className="input-icon"><LockIcon size={16} /></span>
                    <button
                      type="button"
                      className="input-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                  {password && (
                    <>
                      <div className="password-strength">
                        {[1, 2, 3, 4].map(n => (
                          <div key={n} className={`strength-bar ${n <= strength.level ? `active ${strength.cls}` : ''}`} />
                        ))}
                      </div>
                      <div className={`strength-text ${strength.cls}`}>{strength.label}</div>
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm new password</label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <span className="input-icon"><LockIcon size={16} /></span>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <span className="fp-mismatch">Passwords do not match</span>
                  )}
                </div>

                {errorMsg && <div className="fp-error">{errorMsg}</div>}

                <button type="submit" className="auth-submit" disabled={status === 'sending' || !password || !confirmPassword}>
                  {status === 'sending' ? 'Resetting...' : 'Reset password'}
                </button>
              </form>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="fp-success">
              <div className="fp-success-icon">✓</div>
              <div className="auth-form-header">
                <h2>Password reset!</h2>
                <p>Your password has been updated successfully. You can now sign in with your new password.</p>
              </div>
              {/* Phase-1 launch: login disabled — Sign-in button hidden */}
              {/* <Link to="/login" className="auth-submit fp-success-btn">
                Sign in
              </Link> */}
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default ForgotPassword;